import type {
  OAuthErrorEvent,
  OAuthResponseEvent,
  PickerCanceledEvent,
  PickerPickedEvent,
} from '@googleworkspace/drive-picker-element';
import {Menu as MantineMenu} from '@mantine/core';
import React, {lazy, Suspense, useCallback, useState} from 'react';

import {isNodeTesting} from 'lib/Browser';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {
  DRIVE_SCOPE,
  GDriveAuthError,
  GDriveConsentDeniedError,
  GDriveMissingScopesError,
  GDrivePopupClosedError,
  clearLastAuthorizedEmail,
  fetchAndStoreAuthorizedEmail,
  getAccessToken,
  getGDriveOAuthClientId,
  getGDriveProjectNumber,
  getLastAuthorizedEmail,
} from 'notebook/notes/attachments/googleDriveAuth';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

// Used to fetch the user's Google account email after they grant OAuth consent.
const USERINFO_SCOPE = 'https://www.googleapis.com/auth/userinfo.email';

// Matches @width-medium--dropdown from less/variables.less, the legacy ui-library Menu medium width.
const DROPDOWN_MIN_WIDTH = 300;

const GOOGLE_WORKSPACE_MIME_TYPES = new Set([
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
]);

function showGetAccessTokenErrorToast(err: unknown): boolean {
  if (err instanceof GDrivePopupClosedError) {
    return true;
  }
  if (err instanceof GDriveConsentDeniedError) {
    ToastActions.pushToast({
      type: ToastType.WARNING,
      message: 'You denied access to Google Drive. Please try again and allow access to upload files.',
    });
    return true;
  }
  if (err instanceof GDriveMissingScopesError) {
    ToastActions.pushToast({
      type: ToastType.WARNING,
      message:
        'Google Drive file access was not fully granted. Please try again and ensure all requested permissions are accepted.',
    });
    return true;
  }
  if (err instanceof GDriveAuthError) {
    ToastActions.pushToast({
      type: ToastType.DANGER,
      message: 'Unable to connect to Google Drive. Please try again.',
    });
    return true;
  }
  return false;
}

interface DrivePickerInlineProps {
  clientId: string;
  appId: string;
  multiselect: boolean;
  scope: string;
  prompt: '' | 'none' | 'consent' | 'select_account';
  onPicked: (e: PickerPickedEvent) => void;
  onCanceled: (e: PickerCanceledEvent) => void;
  onOauthResponse: (e: OAuthResponseEvent) => void;
  onOauthError: (e: OAuthErrorEvent) => void;
}

// @googleworkspace/drive-picker-react is ESM-only (no CJS "require" export), which causes
// Node's CJS resolver to fail during Mocha test runs. We use a lazy dynamic import gated on
// isNodeTesting so the module is never resolved in tests. This follows the same pattern as
// useRolloutFlags in LaunchDarklyUtil.ts. See also: https://nodejs.org/api/errors.html#err_package_path_not_exported
const DrivePickerLazy = isNodeTesting
  ? () => null
  : lazy(() =>
      import(/* webpackChunkName: "google-drive-picker" */ '@googleworkspace/drive-picker-react').then(
        (m) => ({
          default: function DrivePickerLoaded(props: DrivePickerInlineProps): JSX.Element {
            return (
              <m.DrivePicker
                client-id={props.clientId}
                app-id={props.appId}
                multiselect={props.multiselect}
                scope={props.scope}
                prompt={props.prompt}
                onPicked={props.onPicked}
                onCanceled={props.onCanceled}
                onOauthResponse={props.onOauthResponse}
                onOauthError={props.onOauthError}
              >
                <m.DrivePickerDocsView />
              </m.DrivePicker>
            );
          },
        })
      )
    );

interface UploadDropdownButtonProps {
  onLocalUploadClick: () => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  multiselect?: boolean;
  disabled?: boolean;
}

export default function UploadDropdownButton(props: UploadDropdownButtonProps): JSX.Element {
  const {onLocalUploadClick, onGoogleDriveFilesReady, multiselect = false, disabled = false} = props;

  const [isPickerVisible, setIsPickerVisible] = useState(false);

  const hasGDriveConfig = Boolean(getGDriveOAuthClientId() && getGDriveProjectNumber());

  const handleGDriveClick = useCallback(() => {
    setIsPickerVisible(true);
  }, []);

  const handleCanceled = useCallback((_e: PickerCanceledEvent) => {
    setIsPickerVisible(false);
  }, []);

  const handleOauthResponse = useCallback(async (e: OAuthResponseEvent) => {
    await fetchAndStoreAuthorizedEmail(e.detail.access_token);
  }, []);

  const handleOauthError = useCallback((_e: OAuthErrorEvent) => {
    clearLastAuthorizedEmail();
    setIsPickerVisible(false);
  }, []);

  const handlePicked = useCallback(
    async (e: PickerPickedEvent) => {
      setIsPickerVisible(false);
      const docs = e.detail.docs ?? [];
      if (docs.length === 0) {
        return;
      }

      let accessToken: string;
      try {
        accessToken = await getAccessToken({
          scopes: [DRIVE_SCOPE],
          loginHint: getLastAuthorizedEmail() ?? undefined,
        });
      } catch (err) {
        if (showGetAccessTokenErrorToast(err)) {
          return;
        }
        throw err;
      }

      const files: Array<File> = [];
      for (const doc of docs) {
        const mimeType = doc.mimeType ?? '';
        const isWorkspaceType = GOOGLE_WORKSPACE_MIME_TYPES.has(mimeType);

        const url = isWorkspaceType
          ? `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=application/pdf`
          : `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`;

        const response = await fetch(url, {
          headers: {Authorization: `Bearer ${accessToken}`},
        });

        if (!response.ok) {
          continue;
        }

        const blob = await response.blob();
        const name = doc.name ?? 'untitled';
        const filename = isWorkspaceType ? `${name}.pdf` : name;
        files.push(new File([blob], filename, {type: blob.type}));
      }

      if (files.length > 0) {
        onGoogleDriveFilesReady(files);
      }
    },
    [onGoogleDriveFilesReady]
  );

  return (
    <>
      <MantineMenu position="bottom-start" width={DROPDOWN_MIN_WIDTH} withinPortal={false}>
        <MantineMenu.Target>
          <span>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.SMALL}
              fullWidth={true}
              className="u-tiny-spacing-bottom"
              disabled={disabled}
              text="Upload a File"
              isDropdown={true}
              dataTestAttrs={{component: 'PreviewMixin', element: 'uploadButton'}}
            />
          </span>
        </MantineMenu.Target>
        <MantineMenu.Dropdown>
          <MantineMenu.Item onClick={onLocalUploadClick}>Upload from computer</MantineMenu.Item>
          {hasGDriveConfig && (
            <MantineMenu.Item onClick={handleGDriveClick}>Upload from Google Drive</MantineMenu.Item>
          )}
        </MantineMenu.Dropdown>
      </MantineMenu>
      {hasGDriveConfig && isPickerVisible && (
        <Suspense fallback={null}>
          <DrivePickerLazy
            clientId={getGDriveOAuthClientId()}
            appId={getGDriveProjectNumber()}
            multiselect={multiselect}
            scope={`${USERINFO_SCOPE} ${DRIVE_SCOPE}`}
            prompt=""
            onPicked={handlePicked}
            onCanceled={handleCanceled}
            onOauthResponse={handleOauthResponse}
            onOauthError={handleOauthError}
          />
        </Suspense>
      )}
    </>
  );
}
